import { autocomplete, AutocompleteComponents, getAlgoliaResults } from '@algolia/autocomplete-js'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { createAlgoliaInsightsPlugin } from '@algolia/autocomplete-plugin-algolia-insights'
import { algolia, insights } from 'clients/algolia-client'
import Image from 'next/image'
import { ProductHit } from 'pages/api/v1/algolia/product/build-index'
import { createElement, Fragment, useEffect, useRef } from 'react'
import { render } from 'react-dom'
import formatPrice from 'utils/formatPrice'

const insightsPlugin = createAlgoliaInsightsPlugin({ insightsClient: insights })

const querySuggestionsPlugin = createQuerySuggestionsPlugin({
	searchClient: algolia,
	indexName:
		process.env.NEXT_PUBLIC_ACTIVE_ENV === 'development'
			? 'dev_CCSS_PRODUCTS_QUERY_SUGGESTIONS'
			: 'prod_CCSS_PRODUCTS_QUERY_SUGGESTIONS',
	getSearchParams() {
		return {
			hitsPerPage: 8
		}
	},
	categoryAttribute: [
		process.env.NEXT_PUBLIC_ACTIVE_ENV === 'development' ? 'dev_CCSS_PRODUCTS' : 'prod_CCSS_PRODUCTS',
		'facets',
		'exact_matches',
		'category'
	],
	itemsWithCategories: 1,
	categoriesPerItem: 1,
	transformSource: ({ source, onTapAhead }) => {
		return {
			...source,
			templates: {
				...source.templates,
				item({ item, components }) {
					return (
						<button
							title={`Fill query with "${item.query}"`}
							onClick={(event) => {
								event.preventDefault()
								event.stopPropagation()
								onTapAhead(item)
							}}
						>
							<div className="aa-ItemWrapper">
								<div className="aa-ItemContent">
									<div className="aa-ItemIcon aa-ItemIcon--noBorder">
										<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
											<path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z" />
										</svg>
									</div>
									<div className="aa-ItemContentBody">
										<div className="aa-ItemContentTitle">
											<components.ReverseHighlight hit={item} attribute="query" />
											{item.__autocomplete_qsCategory && (
												<span className="aa-ItemContentSubtitle aa-ItemContentSubtitle--inline">
													<span className="aa-ItemContentSubtitleIcon" /> in{' '}
													<span className="aa-ItemContentSubtitleCategory">
														{item.__autocomplete_qsCategory}
													</span>
												</span>
											)}
										</div>
									</div>
								</div>

								<div className="aa-ItemActions">
									<div className="aa-ItemActionButton">
										<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
											<path d="M8 17v-7.586l8.293 8.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-8.293-8.293h7.586c0.552 0 1-0.448 1-1s-0.448-1-1-1h-10c-0.552 0-1 0.448-1 1v10c0 0.552 0.448 1 1 1s1-0.448 1-1z" />
										</svg>
									</div>
								</div>
							</div>
						</button>
					)
				}
			}
		}
	}
})

export function Autocomplete(props: any) {
	const containerRef = useRef(null)

	useEffect(() => {
		if (!containerRef.current) return undefined

		// @ts-ignore
		const search = autocomplete<ProductHit>({
			container: containerRef.current,
			placeholder: 'Search for products...',
			openOnFocus: true,
			renderer: { createElement, Fragment, render },
			plugins: [querySuggestionsPlugin, insightsPlugin],
			getSources({ query }) {
				if (!query) {
					return []
				}

				return [
					{
						sourceId: 'products',
						getItems() {
							return getAlgoliaResults<ProductHit>({
								searchClient: algolia,
								queries: [
									{
										indexName:
											process.env.NEXT_PUBLIC_ACTIVE_ENV === 'development'
												? 'dev_CCSS_PRODUCTS'
												: 'prod_CCSS_PRODUCTS',
										query,
										params: {
											hitsPerPage: 8,
											clickAnalytics: true,
											attributesToSnippet: ['name:10', 'sku:10'],
											snippetEllipsisText: '…'
										}
									}
								]
							})
						},
						templates: {
							header() {
								return (
									<Fragment>
										<span className="aa-SourceHeaderTitle">Products</span>
										<div className="aa-SourceHeaderLine" />
									</Fragment>
								)
							},
							item({ item, components }) {
								return <ProductItem hit={item} components={components} />
							},
							noResults() {
								return 'No products for this query.'
							}
						}
					}
				]
			}
		})

		return () => {
			search.destroy()
		}
	}, [props])

	return <div className="w-full max-w-lg" ref={containerRef} />
}

type ProductItemProps = {
	hit: ProductHit
	components: AutocompleteComponents
}

function ProductItem({ hit, components }: ProductItemProps) {
	return (
		<a href={`#${hit.slug}`} className="aa-ItemLink">
			<div className="aa-ItemContent">
				<div className="aa-ItemIcon aa-ItemIcon--picture aa-ItemIcon--alignTop relative">
					<Image src={hit.image} alt={hit.name} layout="fill" objectFit="contain" />
				</div>
				<div className="aa-ItemContentBody">
					<div className="font-semibold text-slate-700">
						<components.Highlight hit={hit} attribute="name" />
					</div>
					<div className="text-xs text-slate-500">
						<components.Highlight hit={hit} attribute="sku" />
					</div>
					<div className="aa-ItemContentDescription">
						<strong>{formatPrice(hit.price)}</strong>
					</div>
				</div>
			</div>
		</a>
	)
}
